import Constants from "expo-constants";
import * as Notifications from "expo-notifications";
import type { User as WebUser } from "firebase/auth";
import {
  doc,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import { getApp } from "firebase/app";

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

const PUSH_ASKED_KEY = "push-permission-asked";

function hasAskedForPushPermission(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage?.getItem(PUSH_ASKED_KEY) === "1";
  } catch {
    return false;
  }
}

function markAskedForPushPermission(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(PUSH_ASKED_KEY, "1");
  } catch {
    // localStorage can throw in private mode on some browsers; ignore.
  }
}

export async function registerForPushNotificationsAsync(user: WebUser) {
  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  if (existingStatus !== Notifications.PermissionStatus.GRANTED) {
    // Only auto-prompt once per browser. UNDETERMINED is the state the
    // browser leaves the permission in when the user X's out of the native
    // popup without clicking Allow or Block; without this guard the popup
    // would re-appear on every page load / PWA relaunch.
    if (
      existingStatus === Notifications.PermissionStatus.UNDETERMINED &&
      hasAskedForPushPermission()
    ) {
      return;
    }
    markAskedForPushPermission();
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== "granted") {
    console.warn("Failed to get push token for push notification!");
    return;
  }

  let token: string | null = null;
  try {
    const projectId =
      process.env.EXPO_PUBLIC_EAS_PROJECT_ID ||
      Constants.expoConfig?.extra?.eas?.projectId;

    if (!projectId) {
      console.error("Project ID is missing. Push notifications may fail.");
    }

    try {
      const deviceToken = await Notifications.getDevicePushTokenAsync();
      // Web push needs the entire subscription (endpoint + encryption keys).
      // Prefix identifies it as a web token when the backend fans out.
      token = `WEB_PUSH:${JSON.stringify(deviceToken.data)}`;
    } catch (e) {
      console.error("Failed to get Device Push Token (FCM):", e);
      return;
    }
  } catch (err) {
    console.error("Failed to register for push notifications:", err);
    return;
  }

  if (user && "uid" in user) {
    const firestore = getFirestore(getApp());
    const userRef = doc(firestore, "users", user.uid);
    try {
      // updateDoc fails if the doc doesn't exist — intentional, so that
      // a sign-in attempt without a corresponding Firestore account doesn't
      // create an orphan doc here.
      await updateDoc(userRef, { fcmToken: token });
    } catch (error) {
      console.error("Error saving push token to Firestore:", error);
    }
  } else {
    console.log("User not logged in, cannot save token.");
  }
}
